import { ChargeAccountLineApplication, ChargeAccountTransaction } from 'store/types'

export type LineActivity =
  | { kind: 'transaction'; transaction: ChargeAccountTransaction; index: number }
  | { kind: 'application'; application: ChargeAccountLineApplication; index: number }

const eventTime = (value: Date | string | null | undefined): number => {
  const time = value ? new Date(value).getTime() : Number.NaN

  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time
}

/**
 * A service line's history as one list, oldest first, so a biller reads what
 * was done to the line in the order it was done: payer and patient postings,
 * reversals, write-offs and the patient's applied credit, each at the moment it
 * happened. A posting is placed at its payment date; a write-off, a reversal or
 * an applied credit at the moment it was made. Rows from the same moment keep
 * the order they came in, transactions before credit; a row with no date goes
 * last. `index` is the row's position in the list it came from.
 */
export const lineActivity = (
  transactions: ChargeAccountTransaction[],
  applications: ChargeAccountLineApplication[],
): LineActivity[] => [
  ...transactions.map((transaction, index) => ({
    entry: { kind: 'transaction' as const, transaction, index },
    at: eventTime(transaction.date),
  })),
  ...applications.map((application, index) => ({
    entry: { kind: 'application' as const, application, index },
    at: eventTime(application.effectiveDate),
  })),
]
  .map((row, order) => ({ ...row, order }))
  .sort((a, b) => (a.at === b.at ? a.order - b.order : a.at - b.at))
  .map(({ entry }) => entry)
